/**
 * Component: ConfirmationModal
 * Modal dialog for destructive or important confirmations with optional loading
 * state during async confirmation. Prevents background scroll while open.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onConfirm: () => Promise<void> | void
 * - title: string
 * - message: string
 * - confirmButtonText?: string (default "Delete")
 * - cancelButtonText?: string (default "Cancel")
 * - loadingText?: string (default "Processing...")
 * - isLoading?: boolean (external loading control)
 * - confirmButtonVariant?: 'danger' | 'primary' (default 'danger')
 */
import React, { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { ButtonSpinner } from '../feedback/loading-spinner.jsx';

export function ConfirmationModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmButtonText = "Delete", 
  cancelButtonText = "Cancel",
  loadingText = "Processing...",
  isLoading = false,
  confirmButtonVariant = 'danger'
}) {
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset deleting state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsDeleting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (!isDeleting) {
      setIsDeleting(true);
      try {
        await onConfirm();
        // Don't close the modal here - let the parent component handle it
      } catch (error) {
        console.error('Error in confirmation action:', error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  // Use either the internal state or the prop passed from parent
  const isButtonLoading = isDeleting || isLoading;

  const confirmBase = "inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto";
  const confirmColor = confirmButtonVariant === 'primary'
    ? "bg-blue-600 hover:bg-blue-500"
    : "bg-red-600 hover:bg-red-500";
  const confirmClassName = `${confirmBase} ${confirmColor}`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-0">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={isButtonLoading ? undefined : onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-900 sm:my-8">
        <div className="px-4 pb-4 pt-5 sm:p-6">
          {/* Close button */}
          {!isButtonLoading && (
            <button
              onClick={onClose}
              className="absolute right-4 top-4 rounded-lg p-2 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <X className="h-5 w-5" />
            </button>
          )}

          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10 dark:bg-red-900/20">
              <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
              <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                {title}
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {message}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className={confirmClassName}
              onClick={handleConfirm}
              disabled={isButtonLoading}
            >
              {isButtonLoading ? (
                <span className="flex items-center gap-2">
                  <ButtonSpinner color="white" />
                  {loadingText}
                </span>
              ) : (
                confirmButtonText
              )}
            </button>
            <button
              type="button"
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto dark:bg-gray-800 dark:text-white dark:ring-gray-700 dark:hover:bg-gray-700"
              onClick={onClose}
              disabled={isButtonLoading}
            >
              {cancelButtonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}